/**
 * Paint-surface resolution helpers.
 *
 * A "paint surface" is the (canvas, fabricImage) pair the editor
 * writes layer pixels to when a tool mutates an image/paint
 * layer. The surface map (`paintSurfacesRef` in `useFabricCanvas`)
 * is populated lazily — brush handlers register entries on first
 * stroke; bake ops (filters, Liquify Apply) register on first
 * non-stroke touch via `resolveOrRegisterPaintSurface`. Both code
 * paths converge on the same map so a surface created by either
 * path is visible to the other.
 *
 * Pure module — depends only on DOM canvas APIs. Fabric types are
 * carried structurally so the helpers can be unit-tested without
 * pulling in the Fabric runtime.
 */

/** Minimal structural shape of a Fabric image needed to act as a
 *  paint-surface backer. Mirrors the surface a real
 *  `fabric.FabricImage` exposes; declared structurally so test
 *  doubles don't need to extend Fabric. */
export interface PaintSurfaceFabricLike {
  width?: number;
  height?: number;
  getElement(): HTMLImageElement | HTMLCanvasElement | HTMLVideoElement;
  setElement(el: HTMLCanvasElement): void;
}

export interface PaintSurface<F extends PaintSurfaceFabricLike = PaintSurfaceFabricLike> {
  canvas: HTMLCanvasElement;
  fabricImg: F;
}

/**
 * Convert a Fabric image-like element (`HTMLImageElement` or
 * `HTMLCanvasElement`) into a canvas of the same size with the
 * image content drawn onto it. Returns the existing element if it
 * is already a canvas.
 *
 * Used to make a FabricImage's underlying source paintable:
 * callers write to the returned canvas's 2D context, mark the
 * FabricImage dirty, and the change shows up on next render.
 *
 * `willReadFrequently: true` on the FIRST `getContext('2d', …)`
 * call opts into a CPU-readable backing store — set on first
 * creation because subsequent `getContext` calls return the same
 * context regardless of later options. Skipping this trips the
 * "Multiple readback operations" warning under heavy
 * `getImageData` consumers (adjustment modal, slow-path
 * compositor, brush samplers).
 */
export function ensureCanvasElement(
  el: HTMLImageElement | HTMLCanvasElement | HTMLVideoElement,
  fallbackWidth: number,
  fallbackHeight: number,
): HTMLCanvasElement {
  if (el instanceof HTMLCanvasElement) return el;
  const canvas = document.createElement('canvas');
  canvas.width = (el as HTMLImageElement).naturalWidth || fallbackWidth || 1;
  canvas.height = (el as HTMLImageElement).naturalHeight || fallbackHeight || 1;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (ctx) ctx.drawImage(el, 0, 0);
  return canvas;
}

/**
 * Resolve a paint surface for `layerId`. If an entry already
 * exists in `surfaces`, returns it. Otherwise scans `candidates`
 * for a matching `id`, wraps its image element in a writable
 * canvas via `ensureCanvasElement`, calls `setElement` on the
 * Fabric image so future renders read from the canvas, registers
 * the surface in the map, and returns it.
 *
 * Returns `null` when no candidate matches OR the matching
 * candidate has non-positive dimensions (a partially-initialized
 * FabricImage). Callers should treat a null result as "no
 * surface available, abort the operation."
 *
 * This is the path that makes Liquify (and future bake ops) work
 * on an image layer that has never been brush-touched.
 */
export function resolveOrRegisterPaintSurface<F extends PaintSurfaceFabricLike>(
  layerId: string,
  candidates: Iterable<{ id: string; image: F }>,
  surfaces: Map<string, PaintSurface<F>>,
): PaintSurface<F> | null {
  const existing = surfaces.get(layerId);
  if (existing) return existing;
  for (const { id, image } of candidates) {
    if (id !== layerId) continue;
    const w = image.width ?? 0;
    const h = image.height ?? 0;
    if (w <= 0 || h <= 0) return null;
    const surfaceCanvas = ensureCanvasElement(image.getElement(), w, h);
    if (image.getElement() !== surfaceCanvas) image.setElement(surfaceCanvas);
    const surface: PaintSurface<F> = { canvas: surfaceCanvas, fabricImg: image };
    surfaces.set(layerId, surface);
    return surface;
  }
  return null;
}
